import React, { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, Chip, Grid, TableRow, TableCell,
  DialogContent, DialogActions, TextField, Alert, Tooltip,
  FormControl, InputLabel, Select, MenuItem, TablePagination,
  Divider, CircularProgress, useTheme
} from '@mui/material';
import {
  Add, Edit, Delete, Refresh, Receipt, Settings,
  Search, Schedule, Done, Cancel, Description, PictureAsPdf,
} from '@mui/icons-material';
import api from '../../utils/api';
import {
  SectionHeader, GlassPaper, GlassTable, GlassDialog, GlassDialogSection,
  GradientButton, StatCard, EmptyState, ShimmerTableRows
} from './ui';
import { glass } from './ui/glassTokens';

// Source type labels for display
const SOURCE_TYPE_LABELS = {
  manual: 'Manual',
  library: 'Library',
  institute: 'Institute',
  student: 'Student',
  subscription: 'Subscription',
  wallet_topup: 'Wallet Top-up',
  commission: 'Commission',
  website_plan: 'Website Plan',
  booking: 'Booking',
};

// Status display config
const STATUS_CONFIG = {
  completed: { color: '#10B981', bg: 'rgba(16,185,129,0.15)' },
  draft: { color: '#6366F1', bg: 'rgba(99,102,241,0.15)' },
  cancelled: { color: '#F43F5E', bg: 'rgba(244,63,94,0.15)' },
  refunded: { color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
  failed: { color: '#EF4444', bg: 'rgba(239,68,68,0.15)' },
  pending: { color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
};

// Source type chip colors
const SOURCE_CHIP_CONFIG = {
  manual: { color: '#6366F1', bg: 'rgba(99,102,241,0.12)' },
  library: { color: '#22D3EE', bg: 'rgba(34,211,238,0.12)' },
  institute: { color: '#A78BFA', bg: 'rgba(167,139,250,0.12)' },
  student: { color: '#34D399', bg: 'rgba(52,211,153,0.12)' },
  subscription: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  wallet_topup: { color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
  commission: { color: '#F43F5E', bg: 'rgba(244,63,94,0.12)' },
  website_plan: { color: '#EC4899', bg: 'rgba(236,72,153,0.12)' },
  booking: { color: '#0EA5E9', bg: 'rgba(14,165,233,0.12)' },
};

const DEFAULT_LINE_ITEM = { description: '', qty: 1, rate_rupees: '' };

const DEFAULT_FORM = {
  source_type: 'manual',
  bill_to_name: '',
  bill_to_email: '',
  bill_to_phone: '',
  bill_to_address: '',
  bill_to_gstin: '',
  line_items: [{ ...DEFAULT_LINE_ITEM }],
  gst_rate: 18,
  status: 'completed',
  payment_method: 'Online Payment',
  payment_id: '',
  notes: '',
  invoice_date: '',
};

const DEFAULT_SETTINGS = {
  company_name: '',
  address: '',
  email: '',
  website: '',
  phone: '',
  gstin: '',
};

function InvoicesManagement({ apiBase = '/admin' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Per-row PDF download spinner
  const [downloadingRowId, setDownloadingRowId] = useState(null);

  // Create/Edit dialog
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null); // null = create mode
  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Settings dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsData, setSettingsData] = useState({ ...DEFAULT_SETTINGS });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSubmitting, setSettingsSubmitting] = useState(false);
  const [settingsError, setSettingsError] = useState('');

  // searchQuery is intentionally NOT in the auto-fetch dependency array.
  // Search is manually triggered via Enter / button click (same pattern as WithdrawalManagement).
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page + 1, limit: rowsPerPage });
      if (searchQuery) params.append('search', searchQuery);
      if (sourceTypeFilter) params.append('source_type', sourceTypeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await api.get(`${apiBase}/finance/invoices?${params}`);
      setInvoices(response.data.data.invoices || []);
      setTotalCount(response.data.data.pagination?.total || 0);
    } catch (err) {
      setError('Failed to load invoices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sourceTypeFilter, statusFilter, startDate, endDate]);

  const handleSearch = () => {
    setPage(0);
    fetchInvoices();
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSourceTypeFilter('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  // ── PDF download ─────────────────────────────────────────────────────────────
  const handleDownloadPdf = async (row) => {
    const [source, id] = row.row_id.split(/:(.+)/); // split on first ':' only
    setDownloadingRowId(row.row_id);
    try {
      const response = await api.get(`${apiBase}/finance/invoices/${source}/${id}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${row.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download PDF');
      console.error(err);
    } finally {
      setDownloadingRowId(null);
    }
  };

  // ── Create / Edit dialog ─────────────────────────────────────────────────────
  const openCreateDialog = () => {
    setEditingRow(null);
    setFormData({ ...DEFAULT_FORM, line_items: [{ ...DEFAULT_LINE_ITEM }] });
    setFormError('');
    setInvoiceDialogOpen(true);
  };

  const openEditDialog = (row) => {
    setEditingRow(row);
    /*
     * EDIT PREFILL NOTE:
     * The list endpoint does not return line_items detail. Rather than assuming
     * an unpinned /invoices/:id GET endpoint exists, we seed the form with the
     * known row data (bill_to_name, status) and create one placeholder line item
     * whose rate equals the total_paise converted to rupees with qty=1 so the
     * form is immediately valid. The admin can adjust items before saving.
     * This is the "pragmatic seed" approach specified in the task.
     */
    setFormData({
      ...DEFAULT_FORM,
      bill_to_name: row.bill_to_name || '',
      status: row.status === 'draft' ? 'draft' : 'completed',
      line_items: [
        {
          description: row.invoice_number ? `Invoice ${row.invoice_number}` : 'Invoice item',
          qty: 1,
          rate_rupees: row.total_paise ? (row.total_paise / 100).toFixed(2) : '',
        },
      ],
    });
    setFormError('');
    setInvoiceDialogOpen(true);
  };

  const closeInvoiceDialog = () => {
    setInvoiceDialogOpen(false);
    setEditingRow(null);
    setFormError('');
  };

  // Line item helpers
  const updateLineItem = (index, field, value) => {
    setFormData((prev) => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, line_items: items };
    });
  };

  const addLineItem = () => {
    setFormData((prev) => ({
      ...prev,
      line_items: [...prev.line_items, { ...DEFAULT_LINE_ITEM }],
    }));
  };

  const removeLineItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  };

  // Live totals — computed in paise and rounded to mirror the server exactly
  // (rate_paise = round(rupees*100), gst = round(subtotal*rate/100)) so the
  // preview never disagrees with the persisted figures by a paisa.
  const computeTotals = () => {
    const subtotalPaise = formData.line_items.reduce((sum, item) => {
      const qty = parseFloat(item.qty) || 0;
      const ratePaise = Math.round((parseFloat(item.rate_rupees) || 0) * 100);
      return sum + qty * ratePaise;
    }, 0);
    const gstRate = parseFloat(formData.gst_rate) || 0;
    const gstPaise = Math.round(subtotalPaise * (gstRate / 100));
    const totalPaise = subtotalPaise + gstPaise;
    return {
      subtotalRupees: subtotalPaise / 100,
      gstRupees: gstPaise / 100,
      totalRupees: totalPaise / 100,
    };
  };

  const formatRs = (rupees) =>
    `₹${rupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleInvoiceSubmit = async () => {
    setFormError('');

    // Validation
    if (!formData.bill_to_name.trim()) {
      setFormError('Bill To Name is required');
      return;
    }
    const validItems = formData.line_items.filter(
      (it) => it.description.trim() && parseFloat(it.qty) >= 1
    );
    if (validItems.length === 0) {
      setFormError('At least one line item with description and qty ≥ 1 is required');
      return;
    }

    const body = {
      source_type: formData.source_type,
      bill_to_name: formData.bill_to_name.trim(),
      bill_to_email: formData.bill_to_email || undefined,
      bill_to_phone: formData.bill_to_phone || undefined,
      bill_to_address: formData.bill_to_address || undefined,
      bill_to_gstin: formData.bill_to_gstin || undefined,
      line_items: validItems.map((it) => ({
        description: it.description.trim(),
        qty: parseInt(it.qty, 10),
        rate_paise: Math.round((parseFloat(it.rate_rupees) || 0) * 100),
      })),
      gst_rate: parseFloat(formData.gst_rate) || 18,
      status: formData.status,
      payment_method: formData.payment_method || undefined,
      payment_id: formData.payment_id || undefined,
      notes: formData.notes || undefined,
    };
    if (formData.invoice_date) body.invoice_date = formData.invoice_date;

    try {
      setFormSubmitting(true);
      if (editingRow) {
        const uuid = editingRow.row_id.split(':')[1];
        await api.put(`${apiBase}/finance/invoices/${uuid}`, body);
        setSuccess('Invoice updated successfully');
      } else {
        await api.post(`${apiBase}/finance/invoices`, body);
        setSuccess('Invoice created successfully');
      }
      closeInvoiceDialog();
      fetchInvoices();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save invoice');
    } finally {
      setFormSubmitting(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const openDeleteDialog = (row) => {
    setDeletingRow(row);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingRow) return;
    const uuid = deletingRow.row_id.split(':')[1];
    try {
      setDeleteSubmitting(true);
      await api.delete(`${apiBase}/finance/invoices/${uuid}`);
      setSuccess('Invoice cancelled successfully');
      setDeleteDialogOpen(false);
      setDeletingRow(null);
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel invoice');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // ── Settings ────────────────────────────────────────────────────────────────
  const openSettingsDialog = async () => {
    setSettingsError('');
    setSettingsDialogOpen(true);
    setSettingsLoading(true);
    try {
      const response = await api.get(`${apiBase}/finance/invoices/settings`);
      setSettingsData(response.data.data.settings || { ...DEFAULT_SETTINGS });
    } catch (err) {
      setSettingsError('Failed to load settings');
      console.error(err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSettingsSave = async () => {
    setSettingsError('');
    try {
      setSettingsSubmitting(true);
      await api.put(`${apiBase}/finance/invoices/settings`, settingsData);
      setSuccess('Invoice settings saved');
      setSettingsDialogOpen(false);
    } catch (err) {
      setSettingsError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSettingsSubmitting(false);
    }
  };

  // ── Derived stats from current page data ────────────────────────────────────
  // Only derive what's honestly available: total count from pagination, page counts
  const pageCompleted = invoices.filter((r) => r.status === 'completed').length;
  const pageDraft = invoices.filter((r) => r.status === 'draft').length;
  const pagePending = invoices.filter((r) => r.status === 'pending').length;

  // ── Sx helpers (mirrors WithdrawalManagement) ────────────────────────────────
  const statusChipSx = (status) => {
    const cfg = STATUS_CONFIG[status] || { color: '#94A3B8', bg: 'rgba(148,163,184,0.15)' };
    return {
      fontSize: '0.7rem',
      fontWeight: 600,
      height: 22,
      background: cfg.bg,
      color: cfg.color,
      border: 'none',
    };
  };

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
    '& .MuiSelect-select': {
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiSvgIcon-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
    '& .MuiFormHelperText-root': {
      color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)',
    },
  };

  const paginationSx = {
    color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)',
    '& .MuiSelect-select': { color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' },
    '& .MuiSvgIcon-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
    '& .MuiButtonBase-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
  };

  const dimText = {
    color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)',
  };

  const bodyText = {
    color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    // Bare DATEONLY ("2026-06-18") must be built in local time, else `new Date`
    // parses it as UTC midnight and renders a day early in timezones west of UTC.
    // Full ISO timestamps (with 'T') are parsed normally.
    let d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [y, m, day] = dateString.split('-').map(Number);
      d = new Date(y, m - 1, day);
    } else {
      d = new Date(dateString);
    }
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const { subtotalRupees, gstRupees, totalRupees } = computeTotals();

  return (
    <Box>
      <SectionHeader
        eyebrow="Finance"
        title="Invoices"
        description="View and manage all platform invoices across libraries, students, and manual billing."
        action={
          <Box display="flex" gap={1}>
            <GradientButton
              variant="ghost"
              startIcon={<Settings />}
              onClick={openSettingsDialog}
            >
              Invoice Settings
            </GradientButton>
            <GradientButton
              variant="primary"
              startIcon={<Add />}
              onClick={openCreateDialog}
            >
              Create Invoice
            </GradientButton>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* StatCards — derived only from available data */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Invoices"
            value={totalCount}
            icon={<Receipt />}
            iconIndex={1}
            index={0}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Completed (page)"
            value={pageCompleted}
            icon={<Done />}
            iconIndex={2}
            index={1}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Draft (page)"
            value={pageDraft}
            icon={<Description />}
            iconIndex={0}
            index={2}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Pending (page)"
            value={pagePending}
            icon={<Schedule />}
            iconIndex={3}
            index={3}
          />
        </Grid>
      </Grid>

      {/* Filter bar */}
      <GlassPaper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Search by name or payment ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={handleSearch}
                    sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}
                  >
                    <Search fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>

          {/* Source type */}
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceTypeFilter}
                label="Source"
                onChange={(e) => {
                  setSourceTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(SOURCE_TYPE_LABELS).map(([val, label]) => (
                  <MenuItem key={val} value={val}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status */}
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date range */}
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0);
              }}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0);
              }}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={6} sm={1}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchInvoices}
                disabled={loading}
                sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={1}>
            <GradientButton
              variant="ghost"
              fullWidth
              onClick={handleClearFilters}
            >
              Clear
            </GradientButton>
          </Grid>
        </Grid>
      </GlassPaper>

      {/* Invoices table */}
      <GlassTable
        columns={[
          { label: 'Invoice No', width: 160 },
          { label: 'Date', width: 110 },
          { label: 'Source', width: 140 },
          { label: 'Bill To' },
          { label: 'Amount', align: 'right', width: 110 },
          { label: 'Status', width: 110 },
          { label: 'Actions', align: 'center', width: 120 },
        ]}
        stickyHeader
      >
        {loading ? (
          <ShimmerTableRows count={rowsPerPage} cols={7} />
        ) : invoices.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} sx={{ border: 0, p: 0 }}>
              <EmptyState
                icon={<Receipt />}
                iconIndex={1}
                title="No invoices found"
                description="Create a manual invoice or adjust the filters to find existing records."
                action={
                  <GradientButton variant="primary" startIcon={<Add />} onClick={openCreateDialog}>
                    Create Invoice
                  </GradientButton>
                }
              />
            </TableCell>
          </TableRow>
        ) : (
          invoices.map((row) => {
            const srcCfg = SOURCE_CHIP_CONFIG[row.source_type] || { color: '#94A3B8', bg: 'rgba(148,163,184,0.12)' };
            const isDownloading = downloadingRowId === row.row_id;

            return (
              <TableRow key={row.row_id}>
                {/* Invoice number */}
                <TableCell>
                  <Typography variant="body2" fontWeight="600" sx={{ ...bodyText, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {row.invoice_number}
                  </Typography>
                </TableCell>

                {/* Date */}
                <TableCell sx={{ ...dimText, fontSize: '0.8rem' }}>
                  {row.invoice_date ? formatDate(row.invoice_date) : '-'}
                </TableCell>

                {/* Source chip */}
                <TableCell>
                  <Chip
                    label={row.source_label || SOURCE_TYPE_LABELS[row.source_type] || row.source_type}
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      height: 22,
                      background: srcCfg.bg,
                      color: srcCfg.color,
                      border: 'none',
                    }}
                  />
                </TableCell>

                {/* Bill to */}
                <TableCell>
                  <Typography variant="body2" fontWeight="600" sx={bodyText}>
                    {row.bill_to_name}
                  </Typography>
                  {row.payment_id && (
                    <Typography variant="caption" sx={dimText}>
                      {row.payment_id}
                    </Typography>
                  )}
                </TableCell>

                {/* Amount */}
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="700" sx={{ color: '#6366F1' }}>
                    {row.total_display}
                  </Typography>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Chip
                    label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                    size="small"
                    sx={statusChipSx(row.status)}
                  />
                </TableCell>

                {/* Actions */}
                <TableCell align="center">
                  <Box display="flex" gap={0.5} justifyContent="center" alignItems="center">
                    {/* Download PDF */}
                    <Tooltip title="Download PDF">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadPdf(row)}
                          disabled={isDownloading}
                          sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}
                          aria-label={`Download PDF for invoice ${row.invoice_number}`}
                        >
                          {isDownloading ? (
                            <CircularProgress size={14} />
                          ) : (
                            <PictureAsPdf fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>

                    {/* Edit — only for editable (manual) rows */}
                    {row.editable && (
                      <Tooltip title="Edit Invoice">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(row)}
                          sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}
                          aria-label={`Edit invoice ${row.invoice_number}`}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {/* Delete — only for editable (manual) rows */}
                    {row.editable && (
                      <Tooltip title="Cancel Invoice">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(row)}
                          sx={{ color: '#F43F5E' }}
                          aria-label={`Cancel invoice ${row.invoice_number}`}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </GlassTable>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={paginationSx}
      />

      {/* ── Create / Edit Invoice Dialog ────────────────────────────────────── */}
      <GlassDialog
        open={invoiceDialogOpen}
        onClose={closeInvoiceDialog}
        title={editingRow ? 'Edit Invoice' : 'Create Invoice'}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ pt: 2 }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setFormError('')}>
              {formError}
            </Alert>
          )}

          {/* Source type */}
          <GlassDialogSection label="Invoice Details" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={selectSx}>
                  <InputLabel>Source Type</InputLabel>
                  <Select
                    value={formData.source_type}
                    label="Source Type"
                    onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                  >
                    <MenuItem value="manual">Manual</MenuItem>
                    <MenuItem value="library">Library (manual billing)</MenuItem>
                    <MenuItem value="institute">Institute (manual billing)</MenuItem>
                    <MenuItem value="student">Student (manual billing)</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" sx={{ ...dimText, mt: 0.5, display: 'block' }}>
                  Library, Institute, and Student options are for manually-entered invoices.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Invoice Date (optional)"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                  sx={inputSx}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          {/* Bill To */}
          <GlassDialogSection label="Bill To" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name *"
                  value={formData.bill_to_name}
                  onChange={(e) => setFormData({ ...formData, bill_to_name: e.target.value })}
                  sx={inputSx}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  value={formData.bill_to_email}
                  onChange={(e) => setFormData({ ...formData, bill_to_email: e.target.value })}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone"
                  value={formData.bill_to_phone}
                  onChange={(e) => setFormData({ ...formData, bill_to_phone: e.target.value })}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="GST No (GSTIN)"
                  value={formData.bill_to_gstin}
                  onChange={(e) => setFormData({ ...formData, bill_to_gstin: e.target.value })}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Address"
                  multiline
                  rows={2}
                  value={formData.bill_to_address}
                  onChange={(e) => setFormData({ ...formData, bill_to_address: e.target.value })}
                  sx={inputSx}
                />
              </Grid>
            </Grid>
          {/* Line Items */}
          <GlassDialogSection label="Items" />
            {formData.line_items.map((item, idx) => (
              <Box key={idx} mb={1.5}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={item.description}
                      onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Qty"
                      type="number"
                      inputProps={{ min: 1, step: 1 }}
                      value={item.qty}
                      onChange={(e) => updateLineItem(idx, 'qty', e.target.value)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Rate (₹)"
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={item.rate_rupees}
                      onChange={(e) => updateLineItem(idx, 'rate_rupees', e.target.value)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <Box
                      sx={{
                        ...g.surface,
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.9,
                        textAlign: 'right',
                      }}
                    >
                      <Typography variant="body2" fontWeight="600" sx={{ color: '#6366F1', fontSize: '0.85rem' }}>
                        {formatRs((parseFloat(item.qty) || 0) * (parseFloat(item.rate_rupees) || 0))}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Remove row">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => removeLineItem(idx)}
                          disabled={formData.line_items.length <= 1}
                          sx={{ color: '#F43F5E' }}
                          aria-label="Remove line item"
                        >
                          <Cancel fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Box mt={1}>
              <GradientButton
                variant="ghost"
                startIcon={<Add />}
                onClick={addLineItem}
                size="small"
              >
                Add Row
              </GradientButton>
            </Box>
          {/* GST & Live Preview */}
          <GlassDialogSection label="Tax & Totals" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="GST Rate (%)"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  value={formData.gst_rate}
                  onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                {/* Live preview block */}
                <Box
                  sx={{
                    ...g.surface,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" sx={dimText}>Subtotal</Typography>
                    <Typography variant="body2" sx={bodyText}>{formatRs(subtotalRupees)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" sx={dimText}>
                      GST ({formData.gst_rate || 0}%)
                    </Typography>
                    <Typography variant="body2" sx={bodyText}>{formatRs(gstRupees)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)' }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" fontWeight="700" sx={bodyText}>Total</Typography>
                    <Typography variant="body2" fontWeight="700" sx={{ color: '#6366F1' }}>
                      {formatRs(totalRupees)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          {/* Payment & Notes */}
          <GlassDialogSection label="Payment & Notes" />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" sx={selectSx}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Payment Method"
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  sx={inputSx}
                  placeholder="Online Payment"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Payment ID (optional)"
                  value={formData.payment_id}
                  onChange={(e) => setFormData({ ...formData, payment_id: e.target.value })}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notes (optional)"
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  sx={inputSx}
                />
              </Grid>
            </Grid>        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton variant="ghost" onClick={closeInvoiceDialog} disabled={formSubmitting}>
            Cancel
          </GradientButton>
          <GradientButton
            variant="primary"
            onClick={handleInvoiceSubmit}
            disabled={formSubmitting}
          >
            {formSubmitting
              ? editingRow ? 'Updating...' : 'Creating...'
              : editingRow ? 'Update Invoice' : 'Create Invoice'}
          </GradientButton>
        </DialogActions>
      </GlassDialog>

      {/* ── Delete Confirmation Dialog ──────────────────────────────────────── */}
      <GlassDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDeletingRow(null); }}
        title="Cancel Invoice"
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will soft-cancel the invoice and cannot be undone.
          </Alert>
          <Typography sx={bodyText}>
            Cancel invoice <strong>{deletingRow?.invoice_number}</strong> billed to{' '}
            <strong>{deletingRow?.bill_to_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton
            variant="ghost"
            onClick={() => { setDeleteDialogOpen(false); setDeletingRow(null); }}
            disabled={deleteSubmitting}
          >
            Keep
          </GradientButton>
          <GradientButton variant="danger" onClick={handleDelete} disabled={deleteSubmitting}>
            {deleteSubmitting ? 'Cancelling...' : 'Cancel Invoice'}
          </GradientButton>
        </DialogActions>
      </GlassDialog>

      {/* ── Invoice Settings Dialog ─────────────────────────────────────────── */}
      <GlassDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        title="Invoice Settings"
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ pt: 2 }}>
          {settingsError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSettingsError('')}>
              {settingsError}
            </Alert>
          )}
          {settingsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <GlassDialogSection label="Company Details" />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Company Name"
                    value={settingsData.company_name}
                    onChange={(e) => setSettingsData({ ...settingsData, company_name: e.target.value })}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Address"
                    multiline
                    rows={2}
                    value={settingsData.address}
                    onChange={(e) => setSettingsData({ ...settingsData, address: e.target.value })}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    type="email"
                    value={settingsData.email}
                    onChange={(e) => setSettingsData({ ...settingsData, email: e.target.value })}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone"
                    value={settingsData.phone}
                    onChange={(e) => setSettingsData({ ...settingsData, phone: e.target.value })}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Website"
                    value={settingsData.website}
                    onChange={(e) => setSettingsData({ ...settingsData, website: e.target.value })}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="GSTIN"
                    value={settingsData.gstin}
                    onChange={(e) => setSettingsData({ ...settingsData, gstin: e.target.value })}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton
            variant="ghost"
            onClick={() => setSettingsDialogOpen(false)}
            disabled={settingsSubmitting}
          >
            Cancel
          </GradientButton>
          <GradientButton
            variant="primary"
            onClick={handleSettingsSave}
            disabled={settingsLoading || settingsSubmitting}
          >
            {settingsSubmitting ? 'Saving...' : 'Save Settings'}
          </GradientButton>
        </DialogActions>
      </GlassDialog>
    </Box>
  );
}

export default InvoicesManagement;
